/**
 * Analytics Filters Component
 * Interactive filtering controls for strategic analytics
 * Follows Communication Dashboard design system patterns
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Filter,
  Calendar as CalendarIcon,
  Users,
  MapPin,
  Activity,
  RotateCcw
} from 'lucide-react';
import { subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';

interface DateRange {
  from: Date;
  to: Date;
  label: string;
}

interface AnalyticsFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  className?: string;
}

export interface FilterState {
  dateRange: DateRange;
  activities: string[];
  guides: string[];
  customerType: string;
  location: string;
}

const defaultDateRanges: DateRange[] = [
  {
    from: subDays(new Date(), 30),
    to: new Date(),
    label: 'Last 30 Days'
  },
  {
    from: subDays(new Date(), 90),
    to: new Date(),
    label: 'Last 3 Months'
  },
  {
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
    label: 'This Month'
  },
  {
    from: subMonths(new Date(), 12),
    to: new Date(),
    label: 'Last Year'
  }
];

const mockActivities = [
  'Whale Watching',
  'Safari Tour',
  'Bird Watching',
  'Marine Adventure',
  'Wildlife Photography'
];

const mockGuides = [
  'Sarah Johnson',
  'Mike Wilson',
  'Emma Davis',
  'John Smith',
  'Lisa Anderson'
];

export function AnalyticsFilters({ onFiltersChange, className = "" }: AnalyticsFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    dateRange: defaultDateRanges[0],
    activities: [],
    guides: [],
    customerType: 'all',
    location: 'all'
  });

  const updateFilters = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleDateRangeSelect = (range: DateRange) => {
    updateFilters({ dateRange: range });
  };

  const clearAllFilters = () => {
    const clearedFilters: FilterState = {
      dateRange: defaultDateRanges[0],
      activities: [],
      guides: [],
      customerType: 'all',
      location: 'all'
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.activities.length > 0) count++;
    if (filters.guides.length > 0) count++;
    if (filters.customerType !== 'all') count++;
    if (filters.location !== 'all') count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <Card className={`border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300 ${className}`}>
      <CardContent className="p-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
                <Filter className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Analytics Filters</h3>
                <p className="text-sm text-slate-600">Customize your data view</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-800 border border-blue-200/50">
                  {activeFilterCount} Active
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-600 hover:text-slate-900 h-7 px-2"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset
              </Button>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-900">Date Range</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {defaultDateRanges.map((range, index) => (
                <Button
                  key={index}
                  variant={filters.dateRange.label === range.label ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleDateRangeSelect(range)}
                  className={cn(
                    "text-xs",
                    filters.dateRange.label === range.label
                      ? "bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-sm"
                      : "border-slate-200/80 hover:bg-slate-50"
                  )}
                >
                  {range.label}
                </Button>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              Current: {filters.dateRange.label}
            </div>
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            
            {/* Activities Filter */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-900">Activities</span>
                {filters.activities.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.activities.length}
                  </Badge>
                )}
              </div>
              <Select value={filters.activities[0] || 'all'} onValueChange={(value) => updateFilters({ activities: value && value !== 'all' ? [value] : [] })}>
                <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm text-xs h-8">
                  <SelectValue placeholder="All Activities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activities</SelectItem>
                  {mockActivities.map((activity) => (
                    <SelectItem key={activity} value={activity}>{activity}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Guides Filter */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-900">Guides</span>
              </div>
              <Select value={filters.guides[0] || 'all'} onValueChange={(value) => updateFilters({ guides: value && value !== 'all' ? [value] : [] })}>
                <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm text-xs h-8">
                  <SelectValue placeholder="All Guides" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Guides</SelectItem>
                  {mockGuides.map((guide) => (
                    <SelectItem key={guide} value={guide}>{guide}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Type Filter */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-slate-900">Customer Type</span>
              <Select value={filters.customerType} onValueChange={(value) => updateFilters({ customerType: value })}>
                <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  <SelectItem value="new">New Customers</SelectItem>
                  <SelectItem value="returning">Returning Customers</SelectItem>
                  <SelectItem value="vip">VIP Customers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-900">Location</span>
              </div>
              <Select value={filters.location} onValueChange={(value) => updateFilters({ location: value })}>
                <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="coastal">Coastal Waters</SelectItem>
                  <SelectItem value="inland">Inland Reserve</SelectItem>
                  <SelectItem value="marine">Marine Park</SelectItem>
                  <SelectItem value="forest">Forest Trail</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>

        </div>
      </CardContent>
    </Card>
  );
} 